import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    TextField,
    MenuItem,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    CircularProgress,
    Tabs,
    Tab,
    LinearProgress,
    Tooltip,
    Button
} from '@mui/material';
import {
    CalendarMonth as CalendarIcon,
    CheckCircle as PresentIcon,
    Cancel as AbsentIcon,
    Event as HolidayIcon,
    Download as DownloadIcon,
    Print as PrintIcon
} from '@mui/icons-material';
import { attendanceAPI, classAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useReactToPrint } from 'react-to-print';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const HEAT_COLORS = {
    0: '#ffebee', // No data / Low - Red tint
    1: '#fff3e0', // <50% - Orange tint
    2: '#fff9c4', // 50-75% - Yellow tint
    3: '#e8f5e9', // 75-90% - Light green
    4: '#c8e6c9'  // >90% - Green
};

const CalendarGrid = ({ calendarData, month, year }) => {
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const firstDayOfMonth = new Date(year, month - 1, 1).getDay();

    // Create padding for first week
    const paddedDays = Array(firstDayOfMonth).fill(null);
    const allDays = [...paddedDays, ...calendarData];

    // Split into weeks
    const weeks = [];
    for (let i = 0; i < allDays.length; i += 7) {
        weeks.push(allDays.slice(i, i + 7));
    }

    const getBackgroundColor = (day) => {
        if (!day) return '#f5f5f5';
        if (day.isHoliday) return '#e3f2fd';
        if (day.isSunday) return '#fce4ec';
        return HEAT_COLORS[day.heatLevel] || HEAT_COLORS[0];
    };

    return (
        <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, minWidth: 400 }}>
                {/* Header */}
                {weekDays.map(day => (
                    <Box key={day} sx={{ p: 1, textAlign: 'center', fontWeight: 'bold', bgcolor: '#e0e0e0' }}>
                        {day}
                    </Box>
                ))}

                {/* Calendar cells */}
                {weeks.flat().map((day, idx) => (
                    <Tooltip
                        key={idx}
                        title={day ? (
                            day.isHoliday ? `Holiday: ${day.holidayName}` :
                                day.isSunday ? 'Sunday' :
                                    `Present: ${day.presentCount}, Absent: ${day.absentCount}`
                        ) : ''}
                        arrow
                    >
                        <Box
                            sx={{
                                p: 1,
                                minHeight: 60,
                                bgcolor: getBackgroundColor(day),
                                border: '1px solid #e0e0e0',
                                borderRadius: 1,
                                textAlign: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            {day && (
                                <>
                                    <Typography variant="body2" fontWeight="bold">
                                        {day.dayOfMonth}
                                    </Typography>
                                    {day.isHoliday && <HolidayIcon fontSize="small" color="primary" />}
                                    {!day.isHoliday && !day.isSunday && day.totalCount > 0 && (
                                        <Typography variant="caption" color="textSecondary">
                                            {day.presentCount}/{day.totalCount}
                                        </Typography>
                                    )}
                                </>
                            )}
                        </Box>
                    </Tooltip>
                ))}
            </Box>
        </Box>
    );
};

const CalendarHeatmap = () => {
    const [loading, setLoading] = useState(false);
    const [classes, setClasses] = useState([]);
    const [selectedClass, setSelectedClass] = useState('');
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(new Date().getFullYear());
    const [calendarData, setCalendarData] = useState(null);

    useEffect(() => {
        classAPI.getAll().then(res => setClasses(res.data.data || []));
    }, []);

    useEffect(() => {
        fetchCalendarData();
    }, [month, year, selectedClass]);

    const fetchCalendarData = async () => {
        try {
            setLoading(true);
            const res = await attendanceAPI.getCalendarHeatmap({
                month,
                year,
                classId: selectedClass || undefined
            });
            setCalendarData(res.data.data);
        } catch (error) {
            toast.error('Failed to fetch calendar data');
        } finally {
            setLoading(false);
        }
    };

    const months = [
        { value: 1, label: 'January' },
        { value: 2, label: 'February' },
        { value: 3, label: 'March' },
        { value: 4, label: 'April' },
        { value: 5, label: 'May' },
        { value: 6, label: 'June' },
        { value: 7, label: 'July' },
        { value: 8, label: 'August' },
        { value: 9, label: 'September' },
        { value: 10, label: 'October' },
        { value: 11, label: 'November' },
        { value: 12, label: 'December' }
    ];

    return (
        <Box>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        select
                        label="Class (All)"
                        value={selectedClass}
                        onChange={(e) => setSelectedClass(e.target.value)}
                    >
                        <MenuItem value="">All Classes</MenuItem>
                        {classes.map(cls => (
                            <MenuItem key={cls.id} value={cls.id}>{cls.name}</MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <TextField
                        fullWidth
                        select
                        label="Month"
                        value={month}
                        onChange={(e) => setMonth(e.target.value)}
                    >
                        {months.map(m => (
                            <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <TextField
                        fullWidth
                        label="Year"
                        type="number"
                        value={year}
                        onChange={(e) => setYear(e.target.value)}
                        inputProps={{ min: 2020, max: 2030 }}
                    />
                </Grid>
            </Grid>

            {/* Legend */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Chip size="small" label="Sunday" sx={{ bgcolor: '#fce4ec' }} />
                <Chip size="small" label="Holiday" sx={{ bgcolor: '#e3f2fd' }} icon={<HolidayIcon />} />
                <Chip size="small" label="<50%" sx={{ bgcolor: HEAT_COLORS[1] }} />
                <Chip size="small" label="50-75%" sx={{ bgcolor: HEAT_COLORS[2] }} />
                <Chip size="small" label=">75%" sx={{ bgcolor: HEAT_COLORS[3] }} />
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : calendarData ? (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {months.find(m => m.value === calendarData.month)?.label} {calendarData.year}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            Working Days: {calendarData.workingDays} / {calendarData.totalDays}
                        </Typography>
                        <CalendarGrid
                            calendarData={calendarData.calendar}
                            month={calendarData.month}
                            year={calendarData.year}
                        />
                    </CardContent>
                </Card>
            ) : (
                <Typography>Select month and year to view calendar</Typography>
            )}
        </Box>
    );
};

const ComplianceReport = () => {
    const printRef = useRef();
    const handlePrint = useReactToPrint({
        contentRef: printRef,
    });

    const [loading, setLoading] = useState(false);
    const [classes, setClasses] = useState([]);
    const [selectedClass, setSelectedClass] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [threshold, setThreshold] = useState(75);
    const [reportData, setReportData] = useState(null);

    useEffect(() => {
        classAPI.getAll().then(res => setClasses(res.data.data || []));
        // Set default dates (current month)
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        setStartDate(firstDay.toISOString().split('T')[0]);
        setEndDate(lastDay.toISOString().split('T')[0]);
    }, []);

    const fetchReport = async () => {
        if (!startDate || !endDate) {
            toast.warning('Please select date range');
            return;
        }

        try {
            setLoading(true);
            const res = await attendanceAPI.getComplianceReport({
                startDate,
                endDate,
                classId: selectedClass || undefined,
                threshold
            });
            setReportData(res.data.data);
        } catch (error) {
            toast.error('Failed to fetch compliance report');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (startDate && endDate) {
            fetchReport();
        }
    }, [startDate, endDate, selectedClass, threshold]);

    const handleExportExcel = async () => {
        if (!reportData || !reportData.students || reportData.students.length === 0) {
            toast.warning('No data to export');
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Compliance Report');

        worksheet.mergeCells('A1:G1');
        const titleCell = worksheet.getCell('A1');
        titleCell.value = `Attendance Compliance Report`;
        titleCell.font = { bold: true, size: 16 };
        titleCell.alignment = { horizontal: 'center' };

        worksheet.mergeCells('A2:G2');
        const subtitleCell = worksheet.getCell('A2');
        subtitleCell.value = `Date Range: ${startDate} to ${endDate} | Threshold: ${threshold}% | Compliance Rate: ${reportData.complianceRate}%`;
        subtitleCell.font = { italic: true, size: 12 };
        subtitleCell.alignment = { horizontal: 'center' };

        worksheet.addRow([]);

        const headerRow = worksheet.addRow([
            'Roll No',
            'Name',
            'Class',
            'Present Days',
            'Working Days',
            'Percentage (%)',
            'Status'
        ]);
        headerRow.font = { bold: true };
        headerRow.eachCell(cell => {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF1A9E8F' }
            };
            cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });

        reportData.students.forEach(student => {
            const row = worksheet.addRow([
                student.rollNumber,
                student.name,
                `${student.className} - ${student.sectionName}`,
                student.presentDays,
                student.workingDays,
                `${student.percentage}%`,
                student.isCompliant ? 'Compliant' : 'Non-Compliant'
            ]);
            row.eachCell(cell => {
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };
            });
        });

        worksheet.columns.forEach(column => {
            let maxLength = 0;
            column.eachCell({ includeEmpty: true }, cell => {
                const columnLength = cell.value ? cell.value.toString().length : 0;
                if (columnLength > maxLength) {
                    maxLength = columnLength;
                }
            });
            column.width = Math.max(maxLength + 3, 15);
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `Attendance_Compliance_Report_${startDate}_to_${endDate}.xlsx`);
    };

    return (
        <Box>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={3}>
                    <TextField
                        fullWidth
                        label="Start Date"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <TextField
                        fullWidth
                        label="End Date"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <TextField
                        fullWidth
                        select
                        label="Class (All)"
                        value={selectedClass}
                        onChange={(e) => setSelectedClass(e.target.value)}
                    >
                        <MenuItem value="">All Classes</MenuItem>
                        {classes.map(cls => (
                            <MenuItem key={cls.id} value={cls.id}>{cls.name}</MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <TextField
                        fullWidth
                        label="Threshold %"
                        type="number"
                        value={threshold}
                        onChange={(e) => setThreshold(e.target.value)}
                        inputProps={{ min: 0, max: 100 }}
                    />
                </Grid>
            </Grid>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : reportData ? (
                <>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<DownloadIcon />}
                            onClick={handleExportExcel}
                        >
                            Export Excel
                        </Button>
                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<PrintIcon />}
                            onClick={handlePrint}
                        >
                            Print
                        </Button>
                    </Box>

                    <Box ref={printRef} sx={{ p: 2 }}>
                        <Box sx={{ mb: 2, pb: 1, borderBottom: '2px solid #1a9e8f', display: 'none', displayOnPrint: 'block', textAlign: 'center' }}>
                            <Typography variant="h5" fontWeight="bold" color="primary">
                                Attendance Compliance Report
                            </Typography>
                            <Typography variant="subtitle1" color="textSecondary" sx={{ mt: 0.5 }}>
                                Date Range: {startDate} to {endDate} | Threshold: {threshold}%
                            </Typography>
                        </Box>

                        {/* Summary Cards */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={6} sm={3}>
                                <Card>
                                    <CardContent>
                                        <Typography color="textSecondary" gutterBottom>Total Students</Typography>
                                        <Typography variant="h4">{reportData.totalStudents}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Card>
                                    <CardContent>
                                        <Typography color="textSecondary" gutterBottom>Working Days</Typography>
                                        <Typography variant="h4">{reportData.workingDays}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Card sx={{ bgcolor: '#e8f5e9' }}>
                                    <CardContent>
                                        <Typography color="textSecondary" gutterBottom>Above {threshold}%</Typography>
                                        <Typography variant="h4" color="success.main">
                                            {reportData.aboveThreshold}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Card sx={{ bgcolor: '#ffebee' }}>
                                    <CardContent>
                                        <Typography color="textSecondary" gutterBottom>Below {threshold}%</Typography>
                                        <Typography variant="h4" color="error.main">
                                            {reportData.belowThreshold}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* Compliance Rate Bar */}
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography gutterBottom>Overall Compliance Rate</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={parseFloat(reportData.complianceRate)}
                                        sx={{ flex: 1, height: 20, borderRadius: 2 }}
                                        color={parseFloat(reportData.complianceRate) >= 75 ? 'success' : 'warning'}
                                    />
                                    <Typography variant="h6">{reportData.complianceRate}%</Typography>
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Student Table */}
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Roll No</TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Class</TableCell>
                                        <TableCell align="center">Present Days</TableCell>
                                        <TableCell align="center">Working Days</TableCell>
                                        <TableCell align="center">Percentage</TableCell>
                                        <TableCell align="center">Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reportData.students.map((student, idx) => (
                                        <TableRow key={idx} sx={{ bgcolor: student.isCompliant ? 'inherit' : '#fff3e0' }}>
                                            <TableCell>{student.rollNumber}</TableCell>
                                            <TableCell>{student.name}</TableCell>
                                            <TableCell>{student.className} - {student.sectionName}</TableCell>
                                            <TableCell align="center">{student.presentDays}</TableCell>
                                            <TableCell align="center">{student.workingDays}</TableCell>
                                            <TableCell align="center">
                                                <Typography
                                                    color={parseFloat(student.percentage) >= threshold ? 'success.main' : 'error.main'}
                                                    fontWeight="bold"
                                                >
                                                    {student.percentage}%
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                {student.isCompliant ? (
                                                    <PresentIcon color="success" />
                                                ) : (
                                                    <AbsentIcon color="error" />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </>
            ) : (
                <Typography>Select date range to generate report</Typography>
            )}
        </Box>
    );
};

const AttendanceCalendar = () => {
    const [tabValue, setTabValue] = useState(0);

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon color="primary" />
                Attendance Calendar & Monitoring
            </Typography>

            <Paper sx={{ mt: 2 }}>
                <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                    <Tab label="Calendar Heatmap" />
                    <Tab label="Compliance Report" />
                </Tabs>

                <Box sx={{ p: 3 }}>
                    {tabValue === 0 && <CalendarHeatmap />}
                    {tabValue === 1 && <ComplianceReport />}
                </Box>
            </Paper>
        </Box>
    );
};

export default AttendanceCalendar;
